import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material'
import { FlexCol, FlexRow, RedirectWithQuery } from '@xylabs/sdk-react'
import { AuctionStatus } from '@xyo-network/sdk-xyoworld-js'
import { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'

import { AuctionCardInView, AuctionTableRowInView } from '../../Components'
import { AuctionProvider, ListVariantComponentType, ListVariantContext, ListVariantLoader, MinterAuctionsContext } from '../../Contexts'
import { AuctionListToolbar } from '../../Toolbar'
import WorldBasePage from '../WorldBasePage'

const SortedAuctionsCardList: React.FC = () => {
  const { sortedAuctions } = useContext(MinterAuctionsContext)
  const { size } = useContext(ListVariantContext)
  return (
    <Box display="flex" flexWrap="wrap" justifyContent="center">
      {sortedAuctions
        ? sortedAuctions?.map((sortedAuction) => {
            return (
              <Box display="flex" key={sortedAuction.auction.quadkey.toHex()} margin={0.5}>
                <AuctionCardInView quadkey={sortedAuction.auction.quadkey} size={size} />
              </Box>
            )
          })
        : null}
    </Box>
  )
}

const SortedAuctionsBarList: React.FC = () => {
  const { sortedAuctions } = useContext(MinterAuctionsContext)
  const theme = useTheme()
  const largeUp = useMediaQuery(theme.breakpoints.up('lg'))
  const mediumUp = useMediaQuery(theme.breakpoints.up('md'))
  const smallUp = useMediaQuery(theme.breakpoints.up('sm'))
  return (
    <FlexCol alignItems="stretch">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Id</TableCell>
            {largeUp ? <TableCell align="center">Actions</TableCell> : null}
            {mediumUp ? (
              <TableCell width="100%" align="center">
                Bidder
              </TableCell>
            ) : null}
            {smallUp ? <TableCell align="center">Timer</TableCell> : null}
            <TableCell align="center">Price</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAuctions
            ? sortedAuctions?.map((sortedAuction) => {
                return (
                  <AuctionProvider key={sortedAuction.auction.quadkey.toHex()} quadkey={sortedAuction.auction.quadkey}>
                    <AuctionTableRowInView />
                  </AuctionProvider>
                )
              })
            : null}
        </TableBody>
      </Table>
    </FlexCol>
  )
}

const SortedAuctionsList: React.FC = () => {
  const { componentType } = useContext(ListVariantContext)
  return (
    <FlexCol alignItems="stretch">
      {(() => {
        switch (componentType) {
          case ListVariantComponentType.Bar:
            return <SortedAuctionsBarList />
          case ListVariantComponentType.Card:
            return <SortedAuctionsCardList />
          default:
            throw Error('Invalid ListVariantComponentType')
        }
      })()}
    </FlexCol>
  )
}

interface AuctionsListPageBodyProps {
  requestedFilter?: AuctionStatus
}

const AuctionsListPageBody: React.FC<AuctionsListPageBodyProps> = (props) => {
  const { requestedFilter } = props
  const { sortedAuctions, filter, updateFilter } = useContext(MinterAuctionsContext)

  useEffect(() => {
    if (requestedFilter && filter !== requestedFilter) {
      updateFilter?.(requestedFilter)
    }
  }, [requestedFilter, filter, updateFilter])

  switch (requestedFilter) {
    case AuctionStatus.Available:
    case AuctionStatus.Completed:
    case AuctionStatus.Dutch:
    case AuctionStatus.Live:
    case AuctionStatus.Minted:
    case AuctionStatus.Upcoming:
      break
    default:
      return <RedirectWithQuery to="/auctions" />
  }

  const NoItemsNotification: React.FC = () => (
    <FlexRow display={sortedAuctions?.length === 0 ? 'flex' : 'none'} minHeight={300}>
      <Typography variant="h4">No Auctions</Typography>
    </FlexRow>
  )

  return (
    <FlexCol marginY={1} justifyContent="flex-start" alignItems="stretch">
      <NoItemsNotification />
      <SortedAuctionsList />
    </FlexCol>
  )
}

const AuctionsListPage: React.FC = () => {
  const params = useParams()

  const requestedFilter = params.id as AuctionStatus

  return (
    <ListVariantLoader>
      <WorldBasePage toolbar={<AuctionListToolbar />}>
        <Helmet title="Auction List" />
        <AuctionsListPageBody requestedFilter={requestedFilter} />
      </WorldBasePage>
    </ListVariantLoader>
  )
}

export default AuctionsListPage
